import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import settingsService from '../../services/settingsService';
import './IntegrationsPage.css';

const CATEGORY_INFO = {
  payment: {
    label: 'Payment Gateways',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
      </svg>
    ),
  },
  shipping: {
    label: 'Shipping & Delivery',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
      </svg>
    ),
  },
  marketing: {
    label: 'Marketing & Email',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
  },
  analytics: {
    label: 'Analytics & Tracking',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
};

function IntegrationsPage() {
  const [integrations, setIntegrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectingId, setConnectingId] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [configSettings, setConfigSettings] = useState({});

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await settingsService.getIntegrations();
      setIntegrations(response.data.data);
    } catch (err) {
      toast.error('Failed to load integrations');
      console.error('Error fetching integrations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (integration) => {
    setSelectedIntegration(integration);
    setConfigSettings({});
    setShowConfigModal(true);
  };

  const handleDisconnect = async (integration) => {
    if (!window.confirm(`Are you sure you want to disconnect ${integration.name}? This may affect your store functionality.`)) {
      return;
    }

    setConnectingId(integration.id);
    try {
      await settingsService.updateIntegration(integration.id, {
        isConnected: false,
        settings: null,
      });
      toast.success(`${integration.name} disconnected`);
      fetchIntegrations();
    } catch (err) {
      toast.error(`Failed to disconnect ${integration.name}`);
      console.error('Error disconnecting:', err);
    } finally {
      setConnectingId(null);
    }
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    setConnectingId(selectedIntegration.id);

    try {
      await settingsService.updateIntegration(selectedIntegration.id, {
        isConnected: true,
        settings: configSettings,
      });
      toast.success(`${selectedIntegration.name} connected successfully`);
      setShowConfigModal(false);
      setSelectedIntegration(null);
      fetchIntegrations();
    } catch (err) {
      toast.error(`Failed to connect ${selectedIntegration.name}`);
      console.error('Error connecting:', err);
    } finally {
      setConnectingId(null);
    }
  };

  const handleCloseModal = () => {
    setShowConfigModal(false);
    setSelectedIntegration(null);
    setConfigSettings({});
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getIntegrationsByCategory = () => {
    const grouped = {};
    integrations.forEach((integration) => {
      if (!grouped[integration.category]) {
        grouped[integration.category] = [];
      }
      grouped[integration.category].push(integration);
    });
    return grouped;
  };

  const getConfigFields = (integrationId) => {
    switch (integrationId) {
      case 'stripe':
        return [
          { key: 'publicKey', label: 'Public Key', type: 'text', placeholder: 'pk_live_...' },
          { key: 'secretKey', label: 'Secret Key', type: 'password', placeholder: 'sk_live_...' },
          { key: 'mode', label: 'Mode', type: 'select', options: ['live', 'test'] },
        ];
      case 'paypal':
        return [
          { key: 'clientId', label: 'Client ID', type: 'text', placeholder: 'Your PayPal Client ID' },
          { key: 'clientSecret', label: 'Client Secret', type: 'password', placeholder: 'Your PayPal Client Secret' },
          { key: 'mode', label: 'Mode', type: 'select', options: ['live', 'sandbox'] },
        ];
      case 'mailchimp':
        return [
          { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Your Mailchimp API Key' },
          { key: 'listId', label: 'List ID', type: 'text', placeholder: 'Your default list ID' },
        ];
      case 'google-analytics':
        return [
          { key: 'trackingId', label: 'Tracking ID', type: 'text', placeholder: 'G-XXXXXXXXXX' },
        ];
      case 'royal-mail':
      case 'dpd':
        return [
          { key: 'accountNumber', label: 'Account Number', type: 'text', placeholder: 'Your account number' },
          { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Your API key' },
        ];
      default:
        return [
          { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Your API key' },
        ];
    }
  };

  const getIntegrationInitial = (name) => {
    return name.charAt(0).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="integrations-page">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading integrations...</span>
        </div>
      </div>
    );
  }

  const groupedIntegrations = getIntegrationsByCategory();

  return (
    <div className="integrations-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Integrations</h1>
          <p>Connect third-party services to enhance your store.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="integrations-stats">
        <div className="stat-card">
          <span className="stat-value">{integrations.filter(i => i.isConnected).length}</span>
          <span className="stat-label">Connected</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{integrations.filter(i => !i.isConnected).length}</span>
          <span className="stat-label">Available</span>
        </div>
      </div>

      {/* Integration Categories */}
      {Object.entries(groupedIntegrations).map(([category, items]) => (
        <div key={category} className="integration-category">
          <div className="category-header">
            <div className="category-icon">
              {CATEGORY_INFO[category]?.icon}
            </div>
            <h2>{CATEGORY_INFO[category]?.label || category}</h2>
          </div>

          <div className="integrations-grid">
            {items.map((integration) => (
              <div
                key={integration.id}
                className={`integration-card ${integration.isConnected ? 'connected' : ''}`}
              >
                <div className="integration-header">
                  <div className="integration-logo">
                    {getIntegrationInitial(integration.name)}
                  </div>
                  <div className="integration-info">
                    <h3>{integration.name}</h3>
                    <p>{integration.description}</p>
                  </div>
                  {integration.isConnected && (
                    <div className="connected-badge">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      Connected
                    </div>
                  )}
                </div>

                {integration.isConnected && integration.connectedAt && (
                  <div className="integration-meta">
                    <span>Connected on {formatDate(integration.connectedAt)}</span>
                  </div>
                )}

                <div className="integration-actions">
                  {integration.isConnected ? (
                    <>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleConnect(integration)}
                      >
                        Configure
                      </button>
                      <button
                        className="btn btn-danger-outline"
                        onClick={() => handleDisconnect(integration)}
                        disabled={connectingId === integration.id}
                      >
                        {connectingId === integration.id ? 'Disconnecting...' : 'Disconnect'}
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleConnect(integration)}
                      disabled={connectingId === integration.id}
                    >
                      {connectingId === integration.id ? 'Connecting...' : 'Connect'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Configuration Modal */}
      {showConfigModal && selectedIntegration && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {selectedIntegration.isConnected ? 'Configure' : 'Connect'} {selectedIntegration.name}
              </h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleConfigSubmit}>
              <div className="modal-body">
                <p className="modal-description">
                  Enter your {selectedIntegration.name} credentials to {selectedIntegration.isConnected ? 'update the configuration' : 'enable this integration'}.
                </p>

                {getConfigFields(selectedIntegration.id).map((field) => (
                  <div key={field.key} className="form-group">
                    <label className="form-label">{field.label}</label>
                    {field.type === 'select' ? (
                      <select
                        className="form-select"
                        value={configSettings[field.key] || (selectedIntegration.settings?.[field.key] || field.options[0])}
                        onChange={(e) => setConfigSettings({ ...configSettings, [field.key]: e.target.value })}
                      >
                        {field.options.map((option) => (
                          <option key={option} value={option}>
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        className="form-input"
                        placeholder={field.placeholder}
                        value={configSettings[field.key] || ''}
                        onChange={(e) => setConfigSettings({ ...configSettings, [field.key]: e.target.value })}
                        required={!selectedIntegration.isConnected}
                      />
                    )}
                  </div>
                ))}

                <div className="form-note">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                  <span>Your credentials are encrypted and stored securely.</span>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={connectingId === selectedIntegration.id}
                >
                  {connectingId === selectedIntegration.id
                    ? 'Saving...'
                    : selectedIntegration.isConnected
                      ? 'Save Changes'
                      : 'Connect'
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default IntegrationsPage;
